import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Mail, MessageSquare, Eye, Send } from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Generic Send-Email-or-SMS dialog that reuses the email_templates system.
 * Props:
 *   open, onOpenChange
 *   customer   — { name, email, phone, last_booking_id? }
 *   booking    — optional { booking_id, customer_name, customer_email, customer_phone, child_name, ... }
 *                When present, the merge data uses this booking for live preview.
 *   onSent     — callback after successful send
 *   defaultChannel — 'email' | 'sms' (default: 'email')
 *   titleOverride — string
 */
export const CustomerMessageDialog = ({
  open, onOpenChange, customer, booking, onSent, defaultChannel = 'email', titleOverride,
}) => {
  const [channel, setChannel] = useState(defaultChannel);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [customSubject, setCustomSubject] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [loadingTpls, setLoadingTpls] = useState(false);
  const [sending, setSending] = useState(false);

  // Resolve effective customer details from either prop
  const cust = useMemo(() => {
    if (customer) return customer;
    if (booking) {
      return {
        name: booking.customer_name,
        email: booking.customer_email,
        phone: booking.customer_phone,
      };
    }
    return { name: '', email: '', phone: '' };
  }, [customer, booking]);

  useEffect(() => {
    if (!open) return;
    setChannel(defaultChannel);
    setTemplateId('');
    setCustomMessage('');
    setCustomSubject('');
    setCustomBody('');
    (async () => {
      setLoadingTpls(true);
      try {
        const res = await axios.get(`${API_URL}/api/admin/email-templates`, { withCredentials: true });
        setTemplates(res.data || []);
      } catch {
        toast.error('Failed to load templates');
      } finally {
        setLoadingTpls(false);
      }
    })();
  }, [open, defaultChannel]);

  // Filter templates to those that match the selected channel
  const channelTemplates = useMemo(() => {
    return (templates || []).filter((t) => {
      const c = t.channel || 'email';
      return channel === 'sms' ? c === 'sms' : c !== 'sms';
    });
  }, [templates, channel]);

  const selectedTemplate = useMemo(
    () => channelTemplates.find((t) => t.template_id === templateId) || null,
    [channelTemplates, templateId],
  );

  // Build a merge sample from booking (preferred) or customer
  const previewData = useMemo(() => {
    const sample = {
      customer_name: cust.name || 'Customer',
      customer_email: cust.email || '',
      customer_phone: cust.phone || '',
      child_name: booking?.child_name || 'Child',
      child_age: booking?.child_age || '',
      party_date: booking?.date || '(no upcoming party)',
      party_time: booking?.time_slot === '10:00' ? '10:00 AM' : booking?.time_slot === '13:00' ? '1:00 PM' : booking?.time_slot === '16:00' ? '4:00 PM' : booking?.time_slot || '',
      package_name: booking?.package_name || '',
      guest_count: booking?.guest_count || '',
      balance_due: booking?.balance_due ? Number(booking.balance_due).toFixed(2) : '0.00',
      total_amount: booking?.total ? Number(booking.total).toFixed(2) : '0.00',
      amount_paid: booking?.amount_paid ? Number(booking.amount_paid).toFixed(2) : '0.00',
      invoice_card: '<div style="padding:12px;background:#FFEFF3;border-radius:8px;margin:12px 0"><strong>[Invoice link for this booking]</strong></div>',
      discount_text: '',
      discount_code: '',
    };
    return sample;
  }, [cust, booking]);

  const apply = (txt) => Object.entries(previewData).reduce((a, [k, v]) => a.replaceAll(`{${k}}`, v), txt || '');

  const previewSubject = apply(selectedTemplate ? selectedTemplate.subject || '' : customSubject);
  const previewBody = (() => {
    const base = selectedTemplate ? selectedTemplate.body || '' : customBody;
    let rendered = apply(base);
    if (selectedTemplate && customMessage.trim()) {
      if (channel === 'email') {
        rendered += `<hr style="margin:16px 0;border:0;border-top:1px solid #eee"/><p>${apply(customMessage)}</p>`;
      } else {
        rendered += `\n\n${apply(customMessage)}`;
      }
    }
    return rendered;
  })();

  const canSend = () => {
    if (channel === 'email' && !cust.email) return false;
    if (channel === 'sms' && !cust.phone) return false;
    if (!selectedTemplate && !customBody.trim() && !customMessage.trim()) return false;
    return true;
  };

  const send = async () => {
    if (!canSend()) {
      toast.error(channel === 'email' ? 'Customer email missing' : 'Customer phone missing');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/admin/customer-email`, {
        customer_email: cust.email || '',
        to_phone: cust.phone || '',
        channel,
        template_id: selectedTemplate ? templateId : null,
        custom_subject: selectedTemplate ? '' : customSubject,
        custom_body: selectedTemplate ? '' : customBody,
        custom_message: customMessage,
        booking_id: booking?.booking_id || null,
      }, { withCredentials: true });
      toast.success(channel === 'email' ? `Email sent to ${cust.email}` : `SMS sent to ${cust.phone}`);
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="customer-message-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {channel === 'email' ? <Mail className="w-5 h-5 text-[#FF3B6B]" /> : <MessageSquare className="w-5 h-5 text-[#06D6A0]" />}
            {titleOverride || `Send ${channel === 'email' ? 'Email' : 'SMS'} to ${cust.name || 'Customer'}`}
          </DialogTitle>
          <DialogDescription>
            {channel === 'email' ? cust.email || '(no email on file)' : cust.phone || '(no phone on file)'}
            {booking?.booking_id && <span className="ml-2 text-[11px]">· Booking {booking.booking_id.slice(0, 8)}</span>}
          </DialogDescription>
        </DialogHeader>

        {/* Channel toggle */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs text-[#6B6B7A] mr-1">Channel:</span>
          {['email', 'sms'].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => { setChannel(c); setTemplateId(''); }}
              data-testid={`channel-${c}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                channel === c
                  ? (c === 'email' ? 'bg-[#FF3B6B] text-white' : 'bg-[#06D6A0] text-white')
                  : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:bg-[#FFFDF8]'
              }`}
            >
              {c === 'email' ? <><Mail className="w-3 h-3 inline mr-1" />Email</> : <><MessageSquare className="w-3 h-3 inline mr-1" />SMS</>}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Form */}
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-[#1E1E24]">Template</Label>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="msg-template-select">
                  <SelectValue placeholder={loadingTpls ? 'Loading…' : 'Select a template or leave blank for custom'} />
                </SelectTrigger>
                <SelectContent>
                  {channelTemplates.map((t) => (
                    <SelectItem key={t.template_id} value={t.template_id}>
                      {t.name || t.template_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#6B6B7A] mt-1">Tip: edit templates in Reminders → Email Templates.</p>
            </div>

            {!selectedTemplate && channel === 'email' && (
              <div>
                <Label className="text-xs font-semibold text-[#1E1E24]">Subject (custom)</Label>
                <Input
                  value={customSubject}
                  onChange={(e) => setCustomSubject(e.target.value)}
                  placeholder="Optional subject line"
                  className="mt-1 rounded-xl border-[#E8E5DF]"
                  data-testid="msg-custom-subject"
                />
              </div>
            )}

            {!selectedTemplate && (
              <div>
                <Label className="text-xs font-semibold text-[#1E1E24]">
                  {channel === 'sms' ? 'SMS message' : 'Email body (custom, HTML allowed)'}
                </Label>
                <Textarea
                  value={customBody}
                  onChange={(e) => setCustomBody(e.target.value)}
                  rows={channel === 'sms' ? 5 : 10}
                  placeholder={channel === 'sms' ? 'Hi {customer_name}, just a note…' : '<p>Hi {customer_name}, …</p>'}
                  className="mt-1 rounded-xl border-[#E8E5DF] font-mono text-xs"
                  data-testid="msg-custom-body"
                />
              </div>
            )}

            <div>
              <Label className="text-xs font-semibold text-[#1E1E24]">
                Custom message <span className="font-normal text-[#6B6B7A]">(appended to the template)</span>
              </Label>
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={4}
                placeholder="Anything personal you want to add…"
                className="mt-1 rounded-xl border-[#E8E5DF]"
                data-testid="msg-custom-message"
              />
            </div>
          </div>

          {/* Live preview */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-[#1E1E24] flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Preview</Label>
            <div className="rounded-xl border border-[#E8E5DF] bg-[#FFFDF8] p-4 max-h-[480px] overflow-y-auto" data-testid="msg-preview">
              {channel === 'email' && previewSubject && (
                <div className="text-xs text-[#6B6B7A] mb-2 pb-2 border-b border-[#F0EDE6]">
                  <span className="font-semibold">Subject:</span> {previewSubject}
                </div>
              )}
              {channel === 'sms' ? (
                <pre className="text-sm whitespace-pre-wrap font-sans text-[#1E1E24]">{previewBody || '(empty)'}</pre>
              ) : (
                <div className="text-sm text-[#1E1E24] prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewBody || '<p class="text-[#6B6B7A]">(empty)</p>') }} />
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button
            onClick={send}
            disabled={sending || !canSend()}
            className={`rounded-full text-white ${channel === 'email' ? 'bg-[#FF3B6B] hover:bg-[#E62E58]' : 'bg-[#06D6A0] hover:bg-[#05B88A]'}`}
            data-testid="msg-send-btn"
          >
            <Send className="w-3.5 h-3.5 mr-1" />
            {sending ? 'Sending…' : channel === 'email' ? 'Send Email' : 'Send SMS'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerMessageDialog;
